import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { SingleCard } from "../../layouts";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";

const FORM_ID = "distributor-dialog-form";

const STATUS_OPTIONS = [
  { id: true, name: "Active" },
  { id: false, name: "Inactive" },
];

export default function DistributorForm(props = {}) {
  const {
    resellerId: resellerIdProp,
    onSaved,
    onRequestClose,
  } = props;
  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const resellerIdFromQuery = parseInt(queryParams.get("resellerId"), 10);

  const effectiveResellerId = useMemo(() => {
    if (resellerIdProp != null && resellerIdProp !== "") {
      return Number(resellerIdProp);
    }
    return Number.isFinite(resellerIdFromQuery) ? resellerIdFromQuery : null;
  }, [resellerIdProp, resellerIdFromQuery]);

  const isDialogMode = typeof onRequestClose === "function";

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(
    !!effectiveResellerId && isDialogMode
  );
  const [companyName, setCompanyName] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [nameError, setNameError] = useState("");

  const userCredentials = {
    Barcode: user.barcode,
    Role: user.userRoles,
    Organization: user.org,
    Location: user.location_id,
  };

  useEffect(() => {
    if (!effectiveResellerId) return;

    const loadDistributor = async () => {
      if (isDialogMode) setFetching(true);
      else setLoading(true);
      try {
        const res = await baseAxios.get(`/resellers/${effectiveResellerId}`);
        const reseller = res.data.reseller;
        setCompanyName(reseller.reseller_company_name ?? "");
        setIsActive(reseller.is_active ?? true);
      } catch {
        toast.error("Failed to load distributor data", { duration: 3000 });
      } finally {
        if (isDialogMode) setFetching(false);
        else setLoading(false);
      }
    };

    loadDistributor();
  }, [effectiveResellerId, isDialogMode]);

  const handleSuccess = useCallback(() => {
    if (typeof onSaved === "function") {
      onSaved();
      return;
    }
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  }, [navigate, onSaved]);

  const validateForm = useCallback(() => {
    const trimmed = String(companyName).trim();
    if (!trimmed) {
      setNameError("Please enter a distributor name");
      return false;
    }
    setNameError("");
    return true;
  }, [companyName]);

  const submitPayload = useCallback(async () => {
    if (effectiveResellerId) {
      const res = await baseAxios.put(`/resellers/${effectiveResellerId}`, {
        company_name: String(companyName).trim(),
        is_active: isActive,
      });
      if (res.status === 200) {
        toast.success("Distributor Updated", { duration: 3000 });
        handleSuccess();
      } else {
        toast.error("Distributor update failed, please try again", {
          duration: 5000,
        });
      }
      return;
    }

    const res = await baseAxios.post("/resellers", {
      company_name: String(companyName).trim(),
    });
    if (res.status === 200 || res.status === 201) {
      toast.success("Distributor Created", { duration: 3000 });
      handleSuccess();
    } else {
      toast.error("Distributor creation failed, please try again", {
        duration: 5000,
      });
    }
  }, [companyName, effectiveResellerId, handleSuccess, isActive]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !userCredentials.Role.includes(6) &&
        !userCredentials.Role.includes(3) &&
        !userCredentials.Role.includes(2)
      ) {
        toast.error("User Not authorized, please check your role", {
          duration: 5000,
        });
        return;
      }

      if (!validateForm()) return;

      setLoading(true);
      try {
        await submitPayload();
      } catch (error) {
        if (error.status === 400) {
          toast.error(error.data?.message || "Bad request", { duration: 5000 });
        } else {
          toast.error("Operation failed: " + error.message, { duration: 5000 });
        }
      } finally {
        setLoading(false);
      }
    },
    [submitPayload, userCredentials.Role, validateForm]
  );

  const busy = loading || fetching;

  const formFields = (
    <Stack spacing={2}>
      <TextField
        label="Distributor name"
        value={companyName}
        onChange={(event) => {
          setCompanyName(event.target.value);
          if (nameError) setNameError("");
        }}
        required
        fullWidth
        size="small"
        disabled={busy}
        error={Boolean(nameError)}
        helperText={nameError}
        color="primary"
      />

      {effectiveResellerId ? (
        <FormControl fullWidth size="small" color="primary" disabled={busy}>
          <InputLabel id="distributor-status-label">Status</InputLabel>
          <Select
            labelId="distributor-status-label"
            label="Status"
            value={isActive}
            onChange={(event) => setIsActive(event.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={String(option.id)} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
    </Stack>
  );

  if (isDialogMode) {
    return (
      <>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              {effectiveResellerId ? "Update distributor" : "Add new distributor"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              {effectiveResellerId
                ? "Update the distributor name and status."
                : "Create a new product distributor."}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onRequestClose}
            disabled={busy}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {fetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          ) : (
            <Box
              component="form"
              id={FORM_ID}
              onSubmit={onSubmit}
              sx={{ p: 3 }}
            >
              {formFields}
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={onRequestClose}
            disabled={busy}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="primary"
            disabled={busy || fetching}
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : effectiveResellerId ? (
              "Save distributor"
            ) : (
              "Create distributor"
            )}
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <SingleCard
      title={
        effectiveResellerId ? "Update Distributor" : "Add New Distributor"
      }
    >
      <Box component="form" onSubmit={onSubmit} sx={{ p: 1 }}>
        {formFields}
        <Button
          type="submit"
          variant="contained"
          color="primary"
          fullWidth
          disabled={busy}
          sx={{
            mt: 2.5,
            minHeight: 44,
            textTransform: "none",
            fontWeight: 700,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {loading ? (
            <CircularProgress size={20} color="inherit" />
          ) : effectiveResellerId ? (
            "Save Distributor"
          ) : (
            "Create Distributor"
          )}
        </Button>
      </Box>
    </SingleCard>
  );
}
